import React, { useState } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface WelcomeModalProps {
  onClose: () => void;
}

export default function WelcomeModal({ onClose }: WelcomeModalProps) {
  const [isClosing, setIsClosing] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);

  const handleContinue = () => {
    setButtonPressed(true);
    setTimeout(() => {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 300); // Tempo da animação de fechamento
    }, 200); // Pequeno delay para mostrar o estado "pressionado"
  };

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto transition-opacity duration-300 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

      {/* Modal Container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-5xl w-full overflow-hidden transform transition-all duration-300 ${
            isClosing ? 'scale-95 translate-y-4' : 'scale-100 translate-y-0'
          }`}
        >
          {/* Header Image - Aumentada para exibir completa */}
          <div className="relative h-[400px] w-full overflow-hidden bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
            <img
              src="/login-bg.jpg"
              alt="Hub Machines"
              className="max-w-full max-h-full object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/5 to-black/20" />
          </div>

          {/* Close Button */}
          <button
            onClick={handleContinue}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 transition-colors shadow-lg"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className="px-8 py-6 max-h-[calc(100vh-20rem)] overflow-y-auto">
            {/* Title */}
            <div className="mb-6 text-center">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Bem-vindo ao Hub Machines
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Repositório de Máquinas - Gestão Centralizada da Sua Infraestrutura
              </p>
            </div>

            {/* Welcome Message */}
            <div className="space-y-4 text-gray-700 dark:text-gray-300">
              <p className="text-base leading-relaxed">
                O <strong className="text-blue-600 dark:text-blue-400">Hub Machines</strong> é uma plataforma completa para gerenciamento e monitoramento centralizado de toda a infraestrutura de TI da sua empresa.
              </p>

              <p className="text-base leading-relaxed">
                Com uma visão unificada e em tempo real, você tem controle total sobre:
              </p>

              {/* Features List */}
              <div className="space-y-4 mt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Monitoramento de Status e Localização
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Acompanhe o status online/offline de todas as máquinas, e visualize sua localização geográfica através de um dashboard interativo com mapa. Organize e gere relatórios personalizados de uso por usuários, filiais ou setores
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Gestão de Hardware
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Monitore informações detalhadas de hardware, detecte mudanças não autorizadas, acompanhe o uso dos recursos pelos colaboradores e identifique proativamente potenciais problemas antes que afetem a produtividade.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Controle de Software e Controle de Navegação Web
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Monitore programas instalados, quais programas estão sendo usados em tempo real, navegação web dos usuários e bloqueo de sites não permitidos, tudo controlado centralmente pelos nossos agentes.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-400"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Sistema de Alertas Inteligente
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Receba alertas e avisos personalizados na central do usuário, permitindo que você tenha uma verdadeira equipe de TI ativa em cada estação de trabalho, mesmo remotamente.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-2 w-2 rounded-full bg-green-600 dark:bg-red-500"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      Em Desenvolvimento
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="italic">Suporte a acesso remoto</span> para maior controle, monitoramento e suporte ao usuário em tempo real.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  Para mais informações, testes e solicitações de orçamento, entre em contato:{' '}
                  <a
                    href="mailto:natankimberly97@gmail.com"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    natankimberly97@gmail.com
                  </a>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500 text-center mt-2">
                  Desenvolvido por <span className="font-semibold">devnkimberly</span>
                </p>
              </div>
            </div>
          </div>

          {/* Footer Button */}
          <div className="px-8 py-6 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleContinue}
              disabled={buttonPressed}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 transform ${
                buttonPressed
                  ? 'bg-green-600 dark:bg-green-500 scale-95'
                  : 'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 hover:scale-[1.02] active:scale-95'
              } shadow-lg hover:shadow-xl disabled:cursor-default flex items-center justify-center space-x-2`}
            >
              {buttonPressed ? (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Ótimo!</span>
                </>
              ) : (
                <span>Continuar</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

